import styled from 'styled-components';
import { rhythmLevelParams } from '../../../../../common/src/learn_rules.ts';
import { LearnLevel } from '../../../../../common/src/model.ts';
import { CueSlack as LearnCueSlack } from '../learn_cue_row.tsx';
import { attemptPips, ProgressPipsRow, shortScreen } from '../learn_screen_parts.tsx';
import { SpaceBarHint } from './rhythm_tap_button.tsx';

// The Rhythm Echo screen pieces (issue #3473): the "Pattern 3 of 8 · 70 bpm" row, the shared Learn
// progress row (issue #3518) with the rhythm's own label and test ids; the rows a tapping screen
// is built from; and the generic body, title, subtitle and slack every Learn exercise shares (see
// learn_screen_parts.tsx).
//
// The warm-up hands over to the first pattern mid-session (issue #3523), so the two screens are
// built from the same rows in the same order and nothing may move when it does: the rows live here
// and both screens use them rather than declaring their own.

export const rhythmPatternRowTestId = 'rhythm-pattern-row';
export const rhythmPipTestId = 'rhythm-pip';

// `index` is the current pattern, 0-based; pips before it are done, the one at it is current
export const PatternProgressRow = ({ index, count, level }: { index: number; count: number; level: LearnLevel }) => (
  <ProgressPipsRow
    label={`Pattern ${index + 1} of ${count} · ${rhythmLevelParams[level].bpm} bpm`}
    pips={attemptPips(index, count)}
    rowTestId={rhythmPatternRowTestId}
    pipTestId={rhythmPipTestId}
  />
);

// The generic pieces live in learn_screen_parts.tsx (shared with the Range Finder); re-exported here
// so the rhythm screens keep importing them from this file
export {
  learnScreenMaxWidth as rhythmScreenMaxWidth,
  RowLabel,
  ScreenBody,
  ScreenSubtitle,
  ScreenTitle,
  Slack,
} from '../learn_screen_parts.tsx';

// ---- The rows a tapping screen is built from ---------------------------------------------------
// The pattern screen and the warm-up, in this order: the blocks, the notation, the cue taking the
// slack, the tap button (or the button that ends the warm-up), the hint and the foot row.

// Every row below is `flex: none`, and so is the tap button between them: on a screen too short for
// the lot, the one thing that gives is the slack, and once that is down to the cue row's minimum the
// shortfall belongs to the body, which scrolls (issue #3559). Left to shrink, the tap button
// squashed into an ellipse and the empty foot row collapsed, taking Stop's line with it.

export const BlocksRow = styled.div`
  flex: none;
  margin-top: 20px;

  ${shortScreen} {
    margin-top: 12px;
  }
`;

export const NotationRow = styled.div`
  flex: none;
  margin-top: 10px;
`;

// Takes the slack between the notation and the tap button, so the cue word can fill it: the shared
// cue slack (learn_cue_row.tsx), whose minimum keeps Ready · Set · Go! off the tap button (issue
// #3559), set a little below the notation
export const CueSlack = styled(LearnCueSlack)`
  margin-top: 12px;
`;

// Where the tap button would be on a screen the singer sings to (Sing Rhythm, issue #3723): the
// "Hearing you" meter, showing the voice the microphone is picking up. `flex: none` like the button
// it replaces, so the cue above it takes all the room the button gave up.
export const VoiceMeterRow = styled.div`
  flex: none;
  margin-top: 8px;
`;

// Keeps its line whether or not the hint is showing, so nothing jumps when the warm-up's tap
// button is replaced by Next or Try again
export const HintRow = styled(SpaceBarHint)`
  flex: none;
  min-height: 18px;
  margin-top: 14px;
`;

// Reserves the Stop button's line at the foot of every screen that has one, in every phase and on
// the warm-up too, so nothing moves when Stop appears or when the warm-up hands over to Pattern 1
export const FootRow = styled.div`
  flex: none;
  display: flex;
  justify-content: center;
  height: 32px;
  margin-top: 2px;
`;
